import { VISA_COUNTRIES } from '../config';
import type {
  ActiveVisaCountry,
  ChecklistBlockKey,
  VisaCountry,
} from '../types';

// ---------------------------------------------------------------- реестр

/**
 * Заказ сужен до полей, нужных резолву страны, чтобы не тянуть
 * `@aviata/platform-types` (`Accounts.List.Order` этой форме соответствует).
 */
export interface OrderWithFlights {
  flights: Array<{ arr: { airport: string } }>;
}

export const getVisaCountry = (
  code: string,
  registry: VisaCountry[] = VISA_COUNTRIES,
): VisaCountry | null => {
  const normalized = code.trim().toUpperCase();
  return registry.find(country => country.code === normalized) ?? null;
};

export const getActiveVisaCountries = (
  registry: VisaCountry[] = VISA_COUNTRIES,
): ActiveVisaCountry[] =>
  registry.filter((country): country is ActiveVisaCountry => country.isActive);

export const getActiveVisaCountry = (
  code: string,
  registry: VisaCountry[] = VISA_COUNTRIES,
): ActiveVisaCountry | null => {
  const country = getVisaCountry(code, registry);
  return country?.isActive ? country : null;
};

/**
 * Поиск по локализованному названию, частичное совпадение без учёта регистра.
 * Неактивные страны из выдачи не выкидываются — витрина метит их «Скоро».
 */
export const searchVisaCountries = (
  query: string,
  countryNameOf: (code: string) => string,
  registry: VisaCountry[] = VISA_COUNTRIES,
): VisaCountry[] => {
  const needle = query.trim().toLocaleLowerCase();

  if (!needle) {
    return registry;
  }

  return registry.filter(country =>
    countryNameOf(country.code).toLocaleLowerCase().includes(needle),
  );
};

/**
 * Страна подачи по заказу: первый перелёт — путь «туда», его аэропорт прилёта
 * и есть направление поездки (у обратного перелёта прилёт — домашний).
 */
export const resolveCountryByOrderDestination = (
  order: OrderWithFlights,
  registry: VisaCountry[] = VISA_COUNTRIES,
): ActiveVisaCountry | null => {
  const arrivalAirport = order.flights[0]?.arr.airport;

  if (!arrivalAirport) {
    return null;
  }

  return (
    getActiveVisaCountries(registry).find(country =>
      country.destinationAirports.includes(arrivalAirport),
    ) ?? null
  );
};

// ---------------------------------------------------------------- вход

/** Активный заказ, суженный до полей, нужных входу из заказа. */
export interface VisaEntryOrder extends OrderWithFlights {
  id: string;
}

/**
 * Страна подачи для входа из заказа (`?src=order&orderId=`): находит заказ
 * в списке активных и резолвит страну по направлению. `null` — остаёмся на
 * витрине (заказ не найден или направление не поддерживается).
 */
export const resolveEntryCountry = (
  orders: VisaEntryOrder[],
  orderId: string,
  registry?: VisaCountry[],
): ActiveVisaCountry | null => {
  const entryOrder = orders.find(order => order.id === orderId);

  if (!entryOrder) {
    return null;
  }

  return resolveCountryByOrderDestination(entryOrder, registry);
};

// ---------------------------------------------------------------- шаг

interface ResolveVisaStepInput {
  /** `?step=` из URL — приоритетнее черновика. */
  queryStep: unknown;
  /** `lastStep` из черновика — fallback при заходе по обычной ссылке. */
  lastStep: string | null | undefined;
  country: ActiveVisaCountry;
}

/**
 * Возврат из супераппа ведёт на тот шаг, с которого ушли, даже если миниапп
 * поднялся с нуля: сначала шаг из URL, затем `lastStep` черновика, иначе первый
 * блок страны. Невалидный шаг визард не роняет, а откатывает на первый.
 */
export const resolveVisaStep = ({
  queryStep,
  lastStep,
  country,
}: ResolveVisaStepInput): ChecklistBlockKey => {
  const isCountryStep = (step: unknown): step is ChecklistBlockKey =>
    typeof step === 'string' && (country.blocks as string[]).includes(step);

  if (isCountryStep(queryStep)) {
    return queryStep;
  }

  if (isCountryStep(lastStep)) {
    return lastStep;
  }

  return country.blocks[0];
};
